import { Scope, ScopeConfiguration, ScopeConfigurationErrorType } from './types';
import {
  iterableConfigurationRuleScopeEntityType,
  iterableConfigurationRuleSetupType,
} from '../../../../helpers/types/iterableEnums';
import {
  ConfigurationRuleScopeEntityType,
  ConfigurationRuleScopeMode,
  ProblemSeverity,
  ValidationProblemCode,
  ValidationProblemResponse,
} from '@ydistri/api-sdk';
import { isMainScopePriority } from './utils';

export interface ScopeValidationResult {
  isValid: boolean;
  errorType: ScopeConfigurationErrorType;
  errorMessage: string;
}

export const scopeEntityTitle: Record<ConfigurationRuleScopeEntityType, string> = {
  [ConfigurationRuleScopeEntityType.Regions]: 'Region',
  [ConfigurationRuleScopeEntityType.Stores]: 'Store',
  [ConfigurationRuleScopeEntityType.StoreTypes]: 'Store type',
  [ConfigurationRuleScopeEntityType.Departments]: 'Department',
};

/**
 * The server rule each local copy stands for (W1-W3), so a server answer with the same code replaces
 * the local text instead of showing next to it.
 */
export const scopeErrorProblemCode: Record<
  ScopeConfigurationErrorType,
  ValidationProblemCode | undefined
> = {
  [ScopeConfigurationErrorType.CORRECT]: undefined,
  [ScopeConfigurationErrorType.ERROR]: undefined,
  [ScopeConfigurationErrorType.ALL_CONFIGS_NULL]: ValidationProblemCode.ConfigurationRuleNoSetup,
  [ScopeConfigurationErrorType.EMPTY_MULTISELECT]:
    ValidationProblemCode.ConfigurationRuleEmptySelection,
  [ScopeConfigurationErrorType.ALL_SCOPES_SET_TO_ALL]:
    ValidationProblemCode.ConfigurationRuleAllScopesAll,
};

/**
 * The path the server names for each local copy - the request's shape as the rule validators see it
 * (`setupSet`, `scopes`, `scopes[i].entityIds` with i the scope's index in the request) - so the
 * local copy and the server's answer share one identity (code, target, path).
 */
const localScopePath = (
  scope: Scope,
  errorType: ScopeConfigurationErrorType,
): string | undefined => {
  switch (errorType) {
    case ScopeConfigurationErrorType.ALL_CONFIGS_NULL:
      return 'setupSet';
    case ScopeConfigurationErrorType.ALL_SCOPES_SET_TO_ALL:
      return 'scopes';
    case ScopeConfigurationErrorType.EMPTY_MULTISELECT: {
      // the request lists the scopes in the order of the entities' keys
      const index = Object.values(scope.entities).findIndex(
        e => e.selectionOption !== ConfigurationRuleScopeMode.All && e.selectedIds.length === 0,
      );
      return `scopes[${index}].entityIds`;
    }
    case ScopeConfigurationErrorType.CORRECT:
    case ScopeConfigurationErrorType.ERROR:
      return undefined;
  }
};

/** The local copy as a problem with the server's identity, or nothing when the form is fine. */
export const localScopeProblem = (
  scope: Scope,
  result: ScopeValidationResult,
): ValidationProblemResponse | undefined => {
  const code = scopeErrorProblemCode[result.errorType];
  if (result.isValid || code === undefined) return undefined;
  return {
    code,
    severity: ProblemSeverity.Error,
    message: result.errorMessage,
    path: localScopePath(scope, result.errorType),
  };
};

export const scopeConfigurationErrorStrings: Record<ScopeConfigurationErrorType, string> = {
  [ScopeConfigurationErrorType.CORRECT]: '',
  [ScopeConfigurationErrorType.ERROR]: 'Error in configurations',
  [ScopeConfigurationErrorType.ALL_CONFIGS_NULL]: 'No configuration is set for this scope',
  [ScopeConfigurationErrorType.EMPTY_MULTISELECT]:
    'Selection is empty for specified or excluded mode',
  [ScopeConfigurationErrorType.ALL_SCOPES_SET_TO_ALL]:
    'Store types, stores or departments not specified',
};

/**
 * Validate scope configuration
 */
export function validateScopeConfiguration(
  scopeConfiguration: ScopeConfiguration,
): ScopeValidationResult {
  const allConfigsNull = iterableConfigurationRuleSetupType.every(
    configType => scopeConfiguration[configType] === null,
  );

  if (allConfigsNull) {
    return {
      isValid: false,
      errorType: ScopeConfigurationErrorType.ALL_CONFIGS_NULL,
      errorMessage: 'No configuration is set for this scope',
    };
  }

  return {
    isValid: true,
    errorType: ScopeConfigurationErrorType.CORRECT,
    errorMessage: '',
  };
}

/**
 * Validate complete scope including entities and configuration
 *
 * @param scope - The scope to validate
 * @param options - Validation options
 * @param options.includeConfiguration - Whether to validate configuration values (default: true)
 * @param options.includeScope - Whether to validate scope entities (default: true)
 * @returns Validation result with error type and message
 */
export function validateScope(
  scope: Scope | undefined,
  options: {
    includeConfiguration?: boolean;
    includeScope?: boolean;
  } = {},
): ScopeValidationResult {
  const { includeConfiguration = true, includeScope = true } = options;

  // Early return for undefined scope
  if (!scope) {
    return {
      isValid: false,
      errorType: ScopeConfigurationErrorType.ERROR,
      errorMessage: scopeConfigurationErrorStrings[ScopeConfigurationErrorType.ERROR],
    };
  }

  // Check for empty multiselect (Specified or AllExcept with no selected IDs)
  const emptyMultiselect = iterableConfigurationRuleScopeEntityType.find(
    scopeEntityType =>
      scope.entities[scopeEntityType].selectionOption !== ConfigurationRuleScopeMode.All &&
      scope.entities[scopeEntityType].selectedIds.length === 0,
  );

  if (emptyMultiselect) {
    return {
      isValid: false,
      errorType: ScopeConfigurationErrorType.EMPTY_MULTISELECT,
      errorMessage: scopeConfigurationErrorStrings[ScopeConfigurationErrorType.EMPTY_MULTISELECT],
    };
  }

  // Check if all scopes are set to "All" (only if includeScope is true)
  if (includeScope) {
    const allScopesSetToAll = iterableConfigurationRuleScopeEntityType.every(
      scopeEntityType =>
        scope.entities[scopeEntityType].selectionOption === ConfigurationRuleScopeMode.All,
    );

    if (allScopesSetToAll) {
      return {
        isValid: false,
        errorType: ScopeConfigurationErrorType.ALL_SCOPES_SET_TO_ALL,
        errorMessage:
          scopeConfigurationErrorStrings[ScopeConfigurationErrorType.ALL_SCOPES_SET_TO_ALL],
      };
    }
  }

  // Validate configuration (only if includeConfiguration is true and scope is not being created)
  if (includeConfiguration) {
    return validateScopeConfiguration(scope.configuration);
  }

  // All validations passed
  return {
    isValid: true,
    errorType: ScopeConfigurationErrorType.CORRECT,
    errorMessage: '',
  };
}

/**
 * Get validation error reasons for a specific entity type
 */
export function getScopeEntityErrorReasons(
  priority: number,
  entityType: ConfigurationRuleScopeEntityType,
): string[] {
  const reasons: string[] = [];

  if (!isMainScopePriority(priority)) {
    reasons.push(
      `this ${scopeEntityTitle[entityType].toLowerCase()} is not included in general configuration`,
    );
  }

  if (entityType === ConfigurationRuleScopeEntityType.Stores) {
    reasons.push('store type of this store is not in scope');
    reasons.push('region of this store is not in scope');
  } else if (entityType === ConfigurationRuleScopeEntityType.StoreTypes) {
    reasons.push('all stores in this store type have disabled region');
  }

  return reasons;
}
